import { format as dateFnsFormat } from 'date-fns';
import { startCase, toLower } from 'lodash-es';
import { displayDateFormat } from '@zg-rentals/applications-utils';
import type { CreditReportType } from '@zg-rentals/ts-capps-client';

/**
 * Returns true if the date is a valid "serverDate" formatted as yyyy-MM-dd
 * @param value
 * @returns
 */
export function isServerDate(value: string) {
  return /^(19[7-9][0-9]|2[0-1][0-9]{2})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(value);
}

/**
 * Parses a date in the server format yyyy-MM-dd, converting it to a Date object
 * at 0:00 in machine local time
 */
function parseServerDate(serverDate: string) {
  const dateToken = serverDate.split('-');
  const date = new Date(Number(dateToken[0]), Number(dateToken[1]) - 1, Number(dateToken[2]));
  return date;
}

/**
 * Parses a date that can be either in the server format or in the display format. Returns undefined
 * if the date string was unable to be parsed.
 */
export function parseDate(dateString: Date | string | null | undefined): Date | null | undefined {
  // The value is already a date
  if (dateString instanceof Date) return dateString;

  // We consider an empty string as non-parsable, thus returning undefined
  // We need this check because new Date('') returns 1970-01-01 instead of "Invalid Date"
  if (dateString == '') return undefined;

  // Preserve null or undefined values, (that's why we don't use tripple equals here)
  if (dateString == null) return dateString;

  // The server date format is yyyy-MM-dd and needs to be parsed
  // manually because new Date(yyyy-MM-dd) considers this as an UTC-0 date
  if (isServerDate(dateString)) {
    return parseServerDate(dateString);
  }

  let date = new Date(dateString);
  if (isNaN(date.getTime())) {
    // dateString is not parsable
    return undefined;
  }
  // If the date is UTC-0, we need to convert it to local time to prevent
  // off-by-one day errors
  if (date.getUTCHours() === 0) {
    date = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
  }
  return date;
}

/** Parses a date in any format, including the server format, handling timezone offset correctly and then formats it using the display format */
export function formatDate(date: Date | string | null | undefined) {
  if (typeof date === 'string') {
    date = parseDate(date);
  }

  if (!date) return '';
  return dateFnsFormat(date, displayDateFormat);
}

export function getTitleCase(string: string) {
  return startCase(toLower(string));
}

export const getFullName = (creditReport: CreditReportType) => {
  const {
    First: firstName,
    Middle: middleName,
    Surname: lastName,
    // Spanish surname, hyphenated name, etc.
    SecondSurname: secondSurname,
    // Junior, III, etc.
    Gen: gen,
  } = creditReport.fullCreditProfile?.ConsumerIdentity?.[0]?.Name || {};

  return firstName && lastName
    ? [
        getTitleCase(firstName),
        getTitleCase(middleName || ''),
        getTitleCase(lastName),
        getTitleCase(secondSurname || ''),
        getTitleCase(gen || ''),
      ]
        .filter((name) => name)
        .join(' ')
    : 'name unavailable';
};

export function getCurrencyDisplay(stringAmount = '') {
  const amount = parseFloat(stringAmount.replace(/[^0-9.]/g, ''));

  return !isNaN(amount) ? `$${amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : '';
}
